import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';

import { emptyObject } from 'ui-components/default-props/empty';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

class GridHelperServiceClass {
  flatten(data, isExpanded, hasGroupByRows) {
    if (!isExpanded && !hasGroupByRows) {
      return [this.getPrimaryRow(data)];
    }

    if (!Array.isArray(data)) {
      return [data];
    }

    return data.reduce((acc, datum) => acc.concat(this.flatten(datum, isExpanded)), []);
  }

  getPinnedColumns = createDeepEqualSelector(
    (columns) => columns.filter((column) => column.pinned),
    (filteredColumns) => this.sortColumnsByIndex(filteredColumns),
  );

  getNonPinnedColumns = createDeepEqualSelector(
    (columns) => columns.filter((column) => !column.pinned),
    (filteredColumns) => this.sortColumnsByIndex(filteredColumns),
  );

  getPinnedColumnsWidth = createSelector(this.getPinnedColumns, (pinnedColumns) => {
    return (
      pinnedColumns.reduce((sum, column) => {
        return sum + column.width;
      }, this.getCheckboxColumnWidth()) + 1
    );
  });

  getTotalWidth = createSelector(
    (columns) => columns,
    (columns) => {
      return columns.reduce((sum, column) => {
        return sum + column.width;
      }, this.getCheckboxColumnWidth());
    },
  );

  getColumnsInfo = createSelector(
    (columns) => columns,
    (columns) => {
      if (!columns) {
        return emptyObject;
      }

      return {
        pinnedColumns: this.getPinnedColumns(columns),
        nonPinnedColumns: this.getNonPinnedColumns(columns),
        pinnedColumnsWidth: this.getPinnedColumnsWidth(columns),
        totalWidth: this.getTotalWidth(columns),
      };
    },
  );

  getCheckboxColumnWidth() {
    return 45;
  }

  getRowHeight() {
    return 30;
  }

  getPrimaryRow(data) {
    let primaryRow = data;

    while (Array.isArray(primaryRow)) {
      [primaryRow] = primaryRow;
    }

    return primaryRow;
  }

  getGridRowCount(data, expanded, hasGroupByRows) {
    return this.flatten(data, expanded, hasGroupByRows).length;
  }

  sortColumnsByIndex = createSelector(
    (columns) => columns,
    (columns) => {
      return columns.sort((a, b) => {
        return a.index - b.index;
      });
    },
  );

  getSortedColumn(columns) {
    return columns.find((col) => col.sorted !== undefined);
  }

  getNextColumnSortOrder(currentSort, columns = []) {
    const columnSortOrder = ['ascending', 'descending'];
    const currentIndex = columnSortOrder.indexOf(currentSort);
    if (currentIndex === -1) {
      const previousSortedColumn = this.getSortedColumn(columns);
      const previousIndex = previousSortedColumn ? columnSortOrder.indexOf(previousSortedColumn.sorted) : 0;
      return columnSortOrder[previousIndex];
    }
    return columnSortOrder[(currentIndex + 1) % columnSortOrder.length];
  }
}

export const GridHelperService = new GridHelperServiceClass();
